import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listMemos,
  createMemo,
  updateMemo,
  deleteMemo,
  listTasks,
  createTask,
  updateTask,
  deleteTask,
  type Memo,
  type Task,
} from '../api/notes';

// プロジェクト直下の「メモ」「タスク」パネル。会話（スレッド）ではないので、
// 内容は常に上書き保存する（追記しない）。チーム列でメモ/タスクを選ぶと、
// スレッド列＝この一覧、会話欄＝この編集エリアに置き換わる。

type Kind = 'memos' | 'tasks';
type Item = Memo | Task;

interface Props {
  projectId: number;
  kind: Kind;
  listWidth: number;
  isMobile: boolean;
  mobileLevel: number;
  setMobileLevel: (n: number) => void;
}

export default function NotesPanel({
  projectId,
  kind,
  listWidth,
  isMobile,
  mobileLevel,
  setMobileLevel,
}: Props) {
  const qc = useQueryClient();
  const isTask = kind === 'tasks';
  const label = isTask ? 'タスク' : 'メモ';
  const key = [kind, projectId] as const;

  const listQ = useQuery({
    queryKey: key,
    queryFn: () => (isTask ? listTasks(projectId) : listMemos(projectId)) as Promise<Item[]>,
    refetchInterval: 15_000,
  });
  const items: Item[] = listQ.data ?? [];

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [dirty, setDirty] = useState(false);

  const selected = items.find((i) => i.id === selectedId) ?? null;

  // 選択が変わったら編集フォームへ流し込む（上書き前の現在値）。
  useEffect(() => {
    if (selected) {
      setTitle(selected.title);
      setContent(selected.content ?? '');
      setDirty(false);
    }
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const invalidate = () => qc.invalidateQueries({ queryKey: key });

  const createMut = useMutation({
    mutationFn: () =>
      isTask
        ? createTask(projectId, { title: `新しい${label}` })
        : createMemo(projectId, { title: `新しい${label}` }),
    onSuccess: (row: Item) => {
      invalidate();
      setSelectedId(row.id);
      if (isMobile) setMobileLevel(4);
    },
  });

  const saveMut = useMutation({
    mutationFn: () =>
      isTask
        ? updateTask(selectedId!, { title, content })
        : updateMemo(selectedId!, { title, content }),
    onSuccess: () => {
      invalidate();
      setDirty(false);
    },
  });

  const delMut = useMutation({
    mutationFn: (id: number) => (isTask ? deleteTask(id) : deleteMemo(id)),
    onSuccess: () => {
      invalidate();
      setSelectedId(null);
    },
  });

  const toggleDoneMut = useMutation({
    mutationFn: (t: Task) => updateTask(t.id, { done: !t.done }),
    onSuccess: () => invalidate(),
  });

  // ── 一覧列 ──
  const listCol = (
    <div
      style={
        isMobile
          ? mobileLevel !== 3
            ? { display: 'none' }
            : { ...S.listCol, width: '100%' }
          : { ...S.listCol, width: listWidth }
      }
    >
      {isMobile && (
        <div style={S.mobileBack}>
          <button style={S.mobileBackBtn} onClick={() => setMobileLevel(2)}>‹ チーム</button>
          <span style={S.mobileBackTitle}>{label}</span>
        </div>
      )}
      <div style={S.head}>
        <span style={{ fontWeight: 700, color: '#1d1c1d' }}>
          {isTask ? '🗒 タスク' : '📝 メモ'}
        </span>
        <button
          type="button"
          style={S.newBtn}
          onClick={() => createMut.mutate()}
          disabled={createMut.isPending}
          title={`${label}を追加`}
        >
          ＋ 新規
        </button>
      </div>
      <div style={S.list}>
        {listQ.isLoading && <div style={S.empty}>読み込み中…</div>}
        {!listQ.isLoading && items.length === 0 && (
          <div style={S.empty}>まだ{label}がありません。「＋ 新規」で追加。</div>
        )}
        {items.map((it) => {
          const t = it as Task;
          return (
            <button
              key={it.id}
              style={{ ...S.item, ...(selectedId === it.id ? S.itemActive : null) }}
              onClick={() => {
                setSelectedId(it.id);
                if (isMobile) setMobileLevel(4);
              }}
            >
              {isTask && (
                <span
                  role="checkbox"
                  aria-checked={t.done}
                  style={{ ...S.check, ...(t.done ? S.checkOn : null) }}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleDoneMut.mutate(t);
                  }}
                  title={t.done ? '完了（クリックで戻す）' : '未完了（クリックで完了）'}
                >
                  {t.done ? '✓' : ''}
                </span>
              )}
              <span
                style={{
                  ...S.itemTitle,
                  ...(isTask && t.done ? { color: '#9b9b9b', textDecoration: 'line-through' } : null),
                }}
              >
                {it.title || `（無題の${label}）`}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  // ── 編集エリア（上書き保存） ──
  const editor = (
    <section style={isMobile && mobileLevel !== 4 ? { display: 'none' } : S.editor}>
      {isMobile && (
        <div style={S.mobileBack}>
          <button style={S.mobileBackBtn} onClick={() => setMobileLevel(3)}>
            ‹ {label}一覧
          </button>
        </div>
      )}
      {!selected ? (
        <div style={S.editorEmpty}>
          左の一覧から{label}を選ぶ（または「＋ 新規」で追加）と、ここで編集できます。
          <br />
          <span style={{ fontSize: 12, color: '#9b9b9b' }}>
            ※ {label}は会話ではないので、保存すると内容は上書きされます。
          </span>
        </div>
      ) : (
        <div style={S.editorInner}>
          <div style={S.editorBar}>
            {isTask && (
              <label style={S.doneToggle}>
                <input
                  type="checkbox"
                  checked={(selected as Task).done}
                  onChange={() => toggleDoneMut.mutate(selected as Task)}
                />
                完了
              </label>
            )}
            <button
              type="button"
              style={S.delBtn}
              onClick={() => {
                if (window.confirm(`この${label}を削除しますか？`)) delMut.mutate(selected.id);
              }}
            >
              削除
            </button>
          </div>
          <input
            style={S.titleInput}
            value={title}
            placeholder={`${label}のタイトル`}
            onChange={(e) => {
              setTitle(e.target.value);
              setDirty(true);
            }}
          />
          <textarea
            style={S.contentInput}
            value={content}
            placeholder={isTask ? 'やること・詳細メモ' : '内容'}
            onChange={(e) => {
              setContent(e.target.value);
              setDirty(true);
            }}
          />
          <div style={S.saveRow}>
            <span style={S.hint}>
              {saveMut.isPending
                ? '保存中…'
                : dirty
                  ? '未保存の変更があります'
                  : `保存済み（最終: ${new Date(selected.updatedAt).toLocaleString('ja-JP')}）`}
            </span>
            <button
              type="button"
              style={{ ...S.saveBtn, ...(dirty ? null : S.saveBtnDisabled) }}
              onClick={() => saveMut.mutate()}
              disabled={!dirty || saveMut.isPending}
            >
              上書き保存
            </button>
          </div>
        </div>
      )}
    </section>
  );

  return (
    <>
      {listCol}
      {editor}
    </>
  );
}

const S: Record<string, React.CSSProperties> = {
  listCol: {
    flexShrink: 0,
    borderRight: '1px solid #e7ebf0',
    display: 'flex',
    flexDirection: 'column',
    background: '#fafbfc',
    minWidth: 0,
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 44,
    padding: '0 12px',
    borderBottom: '1px solid #e7ebf0',
    flexShrink: 0,
  },
  newBtn: {
    border: '1px solid #cbd5e1',
    background: '#fff',
    borderRadius: 6,
    padding: '4px 10px',
    fontSize: 12,
    fontWeight: 700,
    color: '#2563eb',
    cursor: 'pointer',
  },
  list: { flex: 1, overflowY: 'auto', padding: 6 },
  empty: { padding: 16, fontSize: 13, color: '#9b9b9b', lineHeight: 1.7 },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    textAlign: 'left',
    border: 'none',
    background: 'transparent',
    borderRadius: 6,
    padding: '8px 10px',
    cursor: 'pointer',
    color: '#1d1c1d',
  },
  itemActive: { background: '#e8f0fe' },
  itemTitle: { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 14 },
  check: {
    width: 18,
    height: 18,
    borderRadius: 4,
    border: '1.5px solid #cbd5e1',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    color: '#fff',
    flexShrink: 0,
  },
  checkOn: { background: '#2bac76', borderColor: '#2bac76' },
  editor: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', background: '#fff' },
  editorEmpty: { padding: 40, fontSize: 14, color: '#616061', lineHeight: 1.9 },
  editorInner: { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, padding: 16, gap: 10 },
  editorBar: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12 },
  doneToggle: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#1d1c1d', cursor: 'pointer' },
  delBtn: {
    border: '1px solid #f1c0cb',
    background: '#fff',
    color: '#e01e5a',
    borderRadius: 6,
    padding: '5px 12px',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
  },
  titleInput: {
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    padding: '10px 12px',
    fontSize: 16,
    fontWeight: 700,
    color: '#1d1c1d',
    outline: 'none',
  },
  contentInput: {
    flex: 1,
    minHeight: 200,
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    lineHeight: 1.7,
    color: '#1d1c1d',
    resize: 'none',
    outline: 'none',
    fontFamily: 'inherit',
  },
  saveRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  hint: { fontSize: 12, color: '#9b9b9b' },
  saveBtn: {
    border: 'none',
    background: '#2563eb',
    color: '#fff',
    borderRadius: 8,
    padding: '9px 20px',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
  },
  saveBtnDisabled: { background: '#cbd5e1', cursor: 'default' },
  mobileBack: { display: 'flex', alignItems: 'center', gap: 8, height: 44, padding: '0 12px', borderBottom: '1px solid #e7ebf0', background: '#f8f8fa', flexShrink: 0 },
  mobileBackBtn: { border: 'none', background: 'transparent', color: '#2563eb', fontSize: 16, fontWeight: 600, cursor: 'pointer', flexShrink: 0, padding: '4px 8px 4px 0', lineHeight: 1 },
  mobileBackTitle: { flex: 1, fontWeight: 700, fontSize: 15, color: '#1d1c1d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
};
